/* ------------------------------------------------------------------------------
 *
 *  # Actividades Management Components
 *
 *  Actividades grid and related functionality using DataTables
 *
 * ---------------------------------------------------------------------------- */

import { 
	initGenericDataTable, 
	refreshDataTable, 
	destroyDataTable 
} from '../integrations/datatables.js';

// Actividades Grid Variables
let actividadesCallbacks = null;
let tiposCategorias = [];
let categorias = [];
let subCategorias = [];
let dotNetRefActividades = null;

// Helper functions
function getTipoCategoriaDisplay(actividad) {
	return actividad.tipoCategoriaNombre || '-';
}

function getCategoriaDisplay(actividad) {
	return actividad.categoriaNombre || '-';
}

function getSubCategoriaDisplay(actividad) {
	return actividad.subCategoriaNombre || '-';
}

function getAmbienteDisplay(actividad) {
	return actividad.ambienteNombre || '-';
}

// Función mejorada para formateo de exportación
function formatExportCell(inner) {
	// Si es null, undefined o vacío
	if (!inner) return inner;
	
	// Si es string, verificar si tiene etiquetas HTML reales
	if (typeof inner === 'string') {
		// Regex para detectar etiquetas HTML válidas (no solo símbolos < >)
		const htmlTagPattern = /<[a-zA-Z][^>]*>/;
		if (!htmlTagPattern.test(inner)) {
			// No es HTML, es texto plano (puede contener < > como símbolos)
			return inner;
		}
	}
	
	// Intentar obtener data-export
	try {
		const $el = $(inner);
		const exportText = $el.attr('data-export');
		if (exportText) {
			return exportText;
		}
	} catch (e) {
		// Si falla jQuery, retornar el contenido original
		return inner;
	}
	
	// Intentar extraer texto del HTML
	try {
		let el = $.parseHTML(inner);
		let result = '';
		if (el) {
			$.each(el, function (index, item) {
				if (item.innerText !== undefined) {
					result = result + item.innerText;
				} else if (item.textContent !== undefined) {
					result = result + item.textContent;
				}
			});
		}
		return result || inner;
	} catch (e) {
		// Si falla el parsing, retornar el contenido original
		return inner;
	}
}

// Actividades-specific DataTable Functions
export function initActividadesTable(actividades) {
	destroyDataTable('actividadesTable');
	
	// Prepare data for DataTable (SOLO 6 COLUMNAS)
	const tableData = actividades.map(actividad => [
		// Actions column (first)
		generateActividadActions(actividad.id),
		actividad.nombre || '-',
		getAmbienteDisplay(actividad),
		getTipoCategoriaDisplay(actividad),
		getCategoriaDisplay(actividad),
		getSubCategoriaDisplay(actividad)
	]);

	const options = {
		data: tableData,
		columns: [
			{ title: 'Acciones', orderable: false, className: 'text-center no-export', width: '10%' },
			{ title: 'Nombre', width: '25%' },
			{ title: 'Ambiente', width: '20%' },
			{ title: 'Tipo Categoría', width: '15%' },
			{ title: 'Categoría', width: '15%' },
			{ title: 'SubCategoría', width: '15%' }
		],
		exportTitle: 'Actividades - Montessori Nova School',
		order: [[3, 'asc']], // Order by Tipo Categoría
		buttons: [
			{
				extend: 'collection',
				className: 'btn btn-flat-primary btn-labeled btn-labeled-start dropdown-toggle me-3 rounded',
				text: '<span class="btn-labeled-icon bg-primary text-white rounded-start"><i class="ph-export"></i></span>Exportar',
				buttons: [
					{
						extend: 'excel',
						text: '<i class="ph-file-xls me-1"></i>Excel',
						className: 'dropdown-item',
						title: 'Actividades - Montessori Nova',
						exportOptions: {
							columns: [1, 2, 3, 4, 5],
							format: {
								body: formatExportCell
							}
						}
					},
					{
						extend: 'copy',
						text: '<i class="ph-clipboard-text me-1"></i>Copiar',
						className: 'dropdown-item',
						exportOptions: {
							columns: [1, 2, 3, 4, 5],
							format: {
								body: formatExportCell
							}
						}
					}
				]
			}
		]
	};

	return initGenericDataTable('actividadesTable', options);
}

function generateActividadActions(actividadId) {
	return `
		<div class="d-inline-flex">
			<a href="javascript:void(0)" class="text-body me-2" onclick="window.sfBlazorSB.actividades.edit(${actividadId})" title="Editar actividad">
				<i class="ph-pencil"></i>
			</a>
			<a href="javascript:void(0)" class="text-body" onclick="window.sfBlazorSB.actividades.delete(${actividadId})" title="Eliminar actividad">
				<i class="ph-trash"></i>
			</a>
		</div>
	`;
}

// Refresh actividades grid
export function refreshActividadesTable(actividades) {
	destroyDataTable('actividadesTable');
	initActividadesTable(actividades);
}

// Set callbacks for actividades grid
export function setActividadesCallbacks(dotNetRef) {
	actividadesCallbacks = dotNetRef;
}

// Actividad action functions
export function editActividad(actividadId) {
	if (actividadesCallbacks) {
		actividadesCallbacks.invokeMethodAsync('EditActividad', actividadId);
	}
}

export function deleteActividad(actividadId) {
	if (actividadesCallbacks) {
		actividadesCallbacks.invokeMethodAsync('DeleteActividad', actividadId);
	}
}

// Apply filters function
export function applyActividadesFilters() {
	const ambienteFilter = document.getElementById('ambienteFilterActividades')?.value || '';
	const tipoCategoriaFilter = document.getElementById('tipoCategoriaFilterActividades')?.value || '';
	const categoriaFilter = document.getElementById('categoriaFilterActividades')?.value || '';
	const subCategoriaFilter = document.getElementById('subCategoriaFilterActividades')?.value || '';

	const table = $('#actividadesTable').DataTable();

	// Clear previous search functions
	$.fn.dataTable.ext.search.splice(0);

	// Add custom search functions
	if (ambienteFilter) {
		$.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			// data[2] es la columna "Ambiente"
			return data[2].toLowerCase().includes(ambienteFilter.toLowerCase());
		});
	}

	if (tipoCategoriaFilter) {
		$.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			// data[3] es la columna "Tipo Categoría"
			return data[3].toLowerCase() === tipoCategoriaFilter.toLowerCase();
		});
	}

	if (categoriaFilter) {
		$.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			// data[4] es la columna "Categoría"
			return data[4].toLowerCase() === categoriaFilter.toLowerCase();
		});
	}

	if (subCategoriaFilter) {
		$.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			// data[5] es la columna "SubCategoría"
			return data[5].toLowerCase() === subCategoriaFilter.toLowerCase();
		});
	}

	// Apply filters
	table.draw();
}

// Clear filters function
export function clearActividadesFilters() {
	// Reset Select2 filters
	$('#ambienteFilterActividades').val('').trigger('change');
	$('#tipoCategoriaFilterActividades').val('').trigger('change');
	$('#categoriaFilterActividades').val('').trigger('change');
	$('#subCategoriaFilterActividades').val('').trigger('change');

	// Clear DataTable custom searches
	const table = $('#actividadesTable').DataTable();
	$.fn.dataTable.ext.search.splice(0);
	table.draw();
}

// Initialize filters with Select2
export function initActividadesFilters(tiposCat, cats, subCats, dotNetRef) {
	// Store data
	tiposCategorias = tiposCat || [];
	categorias = cats || [];
	subCategorias = subCats || [];
	dotNetRefActividades = dotNetRef;

	// Initialize Select2 for Ambiente (static data)
	initAmbienteSelect2();

	// Initialize Select2 for Tipo Categoría
	initTipoCategoriaSelect2();

	// Setup cascading logic
	setupCascadingFilters();
}

function initAmbienteSelect2() {
	if (!$().select2) return;

	$('#ambienteFilterActividades').select2({
		placeholder: 'Selecciona un ambiente',
		allowClear: true,
		dropdownParent: $('.sidebar-secondary')
	});
}

function initTipoCategoriaSelect2() {
	if (!$().select2 || !tiposCategorias) return;

	// Populate options
	const $select = $('#tipoCategoriaFilterActividades');
	$select.empty();
	$select.append(new Option('Todos los tipos', '', true, true));

	tiposCategorias.forEach(tc => {
		$select.append(new Option(tc.nombre, tc.nombre, false, false));
	});

	// Initialize Select2
	$select.select2({
		placeholder: 'Selecciona un tipo',
		allowClear: true,
		dropdownParent: $('.sidebar-secondary')
	});
}

function setupCascadingFilters() {
	// When Tipo Categoría changes, update Categorías
	$('#tipoCategoriaFilterActividades').on('change', function() {
		const selectedTipo = $(this).val();
		updateCategoriasFilter(selectedTipo);
	});

	// When Categoría changes, update SubCategorías
	$('#categoriaFilterActividades').on('change', function() {
		const selectedCategoria = $(this).val();
		updateSubCategoriasFilter(selectedCategoria);
	});
}

function updateCategoriasFilter(tipoNombre) {
	const $select = $('#categoriaFilterActividades');
	
	// Clear current options
	$select.empty();
	$select.append(new Option('Todas las categorías', '', true, true));

	if (!tipoNombre) {
		// No filter, show all
		categorias.forEach(cat => {
			$select.append(new Option(cat.nombre, cat.nombre, false, false));
		});
	} else {
		// Filter by Tipo Categoría
		const filtered = categorias.filter(cat => cat.tipoCategoriaNombre === tipoNombre);
		filtered.forEach(cat => {
			$select.append(new Option(cat.nombre, cat.nombre, false, false));
		});
	}

	// Reinitialize Select2
	if ($select.hasClass('select2-hidden-accessible')) {
		$select.select2('destroy');
	}
	$select.select2({
		placeholder: 'Selecciona una categoría',
		allowClear: true,
		dropdownParent: $('.sidebar-secondary')
	});

	// Clear subcategorías
	updateSubCategoriasFilter('');
}

function updateSubCategoriasFilter(categoriaNombre) {
	const $select = $('#subCategoriaFilterActividades');
	
	// Clear current options
	$select.empty();
	$select.append(new Option('Todas las subcategorías', '', true, true));

	if (!categoriaNombre) {
		// No filter, show all
		subCategorias.forEach(subCat => {
			$select.append(new Option(subCat.nombre, subCat.nombre, false, false));
		});
	} else {
		// Filter by Categoría
		const filtered = subCategorias.filter(subCat => subCat.categoriaNombre === categoriaNombre);
		filtered.forEach(subCat => {
			$select.append(new Option(subCat.nombre, subCat.nombre, false, false));
		});
	}

	// Reinitialize Select2
	if ($select.hasClass('select2-hidden-accessible')) {
		$select.select2('destroy');
	}
	$select.select2({
		placeholder: 'Selecciona una subcategoría',
		allowClear: true,
		dropdownParent: $('.sidebar-secondary')
	});
}

